"use client"

import { KeyboardEvent, useEffect, useRef, useState } from "react"
import { extractAndRecordCouponErrorMessage } from "src/components/checkout/hooks/extractCouponErrorMessage"
import { addCouponOrThrow } from "src/components/checkout/hooks/useCart"
import { useCoupons } from "src/components/checkout/hooks/useCoupon"
import { Button } from "src/components/generic/Button"
import { Input } from "src/components/generic/Input"
import { useEventCallback } from "src/components/hooks/useEventCallback"
import { normalizeCouponCode } from "src/utilities/coupons"

export const CouponInput = () => {
  const { codes } = useCoupons()
  const ref = useRef<HTMLInputElement>(null)
  const [value, setValue] = useState("")
  const [adding, setAdding] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [errorMessage, setErrorMessage] = useState("")

  useEffect(() => {
    codes // mark as dependency

    setValue("")
    setAdding(false)
    setDeleting(false)
    setErrorMessage("")
  }, [codes])

  const tryAddCoupon = useEventCallback(async () => {
    if (codes.includes(normalizeCouponCode(value))) {
      setErrorMessage("Dieser Rabattcode wurde bereits angewendet.")
      return
    }

    if (!value) {
      setErrorMessage("")
      return
    }

    setAdding(true)
    setErrorMessage("")

    try {
      await addCouponOrThrow(value)
    } catch (error) {
      const message = extractAndRecordCouponErrorMessage(value, error)
      setErrorMessage(message)
    } finally {
      setAdding(false)
    }
  })

  const onChange = useEventCallback((value: string) => {
    setValue(value)
    setErrorMessage("")
  })

  const onKeyDown = useEventCallback((event: KeyboardEvent) => {
    if (event.key === "Enter") {
      tryAddCoupon()
    }
  })

  return (
    <div className="relative w-full" inert={adding || deleting}>
      <Input
        ref={ref}
        color="gray"
        placeholder="Rabattcode eingeben"
        autoComplete="off"
        value={value}
        error={errorMessage}
        showError
        onChange={onChange}
        onKeyDown={onKeyDown}
        className="pr-32"
      />

      <div className="absolute top-1 right-1">
        <Button size="sm" color="gray10" onClick={tryAddCoupon} disabled={!value} loading={adding || deleting}>
          Anwenden
        </Button>
      </div>
    </div>
  )
}
